import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useCreateCoupon, useCreateVoucher, useGetCampaigns, useGetVoucherByCampaign, useGetVoucherById, useUpdateVoucher } from '../../queries/campaign';
import { useGetProductsList } from '../../queries/products';
import { useGetBrands } from '../../queries/brand';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useEffect, useState } from 'react';
import NewDropdown from '../../components/Forms/NewDropdown';
import * as XLSX from 'xlsx';
import { useLocation, useNavigate } from 'react-router-dom';

const CreateCoupons = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const couponId = state?.couponId || null;

    const { data: couponData, isLoading: isCouponDataLoading } = useGetVoucherById(couponId);

    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const { data: campaignDetails, isLoading: isCampaignDetailsLoading } = useGetVoucherByCampaign(selectedCampaignId);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
    } = useForm({
        defaultValues: {
            couponCode: '',
            campaign: '',
        }
    });

    const [active, setActive] = useState(true);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [couponCodes, setCouponCodes] = useState([]);
    const [uploadedCouponData, setUploadedCouponData] = useState([]);
    const [validationSummary, setValidationSummary] = useState({
        totalRows: 0,
        validRows: 0,
        invalidRows: 0
    });
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [editingCouponCode, setEditingCouponCode] = useState('');
    const [editingBrandName, setEditingBrandName] = useState('');
    const [editingTypeOfDiscount, setEditingTypeOfDiscount] = useState('');
    const [editingDiscountValue, setEditingDiscountValue] = useState('');
    const [editingMaxRedemptionCount, setEditingMaxRedemptionCount] = useState('');
    const [editingMaxCouponCount, setEditingMaxCouponCount] = useState('');
    const [editingPointsRequired, setEditingPointsRequired] = useState('');

    // Fetch data for select options
    const { data: campaigns = [] } = useGetCampaigns();
    const { data: productsList = [] } = useGetProductsList();
    const { data: brandsList = [] } = useGetBrands();

    // const { mutateAsync: createCoupon, isPending: isCreating } = useCreateVoucher();
    // const { mutateAsync: updateCoupon, isPending: isUpdating } = useUpdateVoucher();
    const { mutateAsync: createCoupon, isPending: isCreating } = useCreateCoupon();

    const watchedCampaign = watch("campaign");

    // Handle campaign selection
    const handleCampaignChange = (value) => {
        setValue("campaign", value);
        setSelectedCampaignId(value);
    };

    // Function to handle Excel file upload and processing
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check if file is Excel format
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(fileExtension)) {
            toast.error('Please upload a valid Excel file (.xlsx or .xls)');
            return;
        }

        setUploadedFile(file);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Get the first worksheet
                const worksheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[worksheetName];

                // Convert to JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // Process the data
                const processedData = [];
                
                // Check if this is a brand promotion campaign
                const promotionType = campaignDetails?.campaign?.promotionType;
                const isBrandPromotion = promotionType === 'brand';
                
                jsonData.forEach((row, index) => {
                    // Skip header row
                    if (index === 0) return;
                    
                    if (isBrandPromotion) {
                        // For brand promotion: BrandId, Brand Name, Coupon Code, Max Redemption Count, Max Coupon Count, Type of Discount, Discount Value, Points Required
                        if (row.length >= 8 && row[0] !== undefined && row[0] !== null && row[0] !== '' &&
                            row[1] !== undefined && row[1] !== null && row[1] !== '' &&
                            row[2] !== undefined && row[2] !== null && row[2] !== '' &&
                            row[3] !== undefined && row[3] !== null && row[3] !== '' &&
                            row[4] !== undefined && row[4] !== null && 
                            row[5] !== undefined && row[5] !== null && 
                            row[6] !== undefined && row[6] !== null && row[6] !== '' &&
                            row[7] !== undefined && row[7] !== null) {
                            
                            const brandId = row[0].toString().trim();
                            const brandName = row[1].toString().trim();
                            const couponCode = row[2].toString().trim();
                            const maxRedemptionCount = row[3].toString().trim();
                            const maxCouponCount = row[4].toString().trim();
                            const typeOfDiscount = row[5].toString().trim();
                            const discountValue = row[6].toString().trim();
                            const pointsRequired = row[7].toString().trim();
                            
                            // Find brand details from brandsList
                            const brandDetails = brandsList.find(b => b._id === brandId);
                            let formattedBrandName = brandName;
                            
                            if (brandDetails) {
                                formattedBrandName = brandDetails.brandName || brandName;
                            }
                            
                            processedData.push({
                                brandId,
                                brandName: formattedBrandName,
                                couponCode,
                                maxRedemptionCount,
                                maxCouponCount,
                                typeOfDiscount: typeOfDiscount.toLowerCase(),
                                discountValue,
                                pointsRequired
                            });
                        }
                    } else {
                        // For product promotion: ProductId, ProductName, CouponCode, MaxRedemptionCount, MaxCouponCount, TypeOfDiscount, DiscountValue, PointsRequired
                        if (row.length >= 8 && row[0] !== undefined && row[0] !== null && row[0] !== '' &&
                            row[1] !== undefined && row[1] !== null && row[1] !== '' &&
                            row[2] !== undefined && row[2] !== null && row[2] !== '' &&
                            row[3] !== undefined && row[3] !== null && row[3] !== '' &&
                            row[4] !== undefined && row[4] !== null && 
                            row[5] !== undefined && row[5] !== null && 
                            row[6] !== undefined && row[6] !== null && row[6] !== '' &&
                            row[7] !== undefined && row[7] !== null) {
                            
                            const productId = row[0].toString().trim();
                            const productName = row[1].toString().trim();
                            const couponCode = row[2].toString().trim();
                            const maxRedemptionCount = row[3].toString().trim();
                            const maxCouponCount = row[4].toString().trim();
                            const typeOfDiscount = row[5].toString().trim();
                            const discountValue = row[6].toString().trim();
                            const pointsRequired = row[7].toString().trim();
                            
                            // Find product details from productsList
                            const productDetails = productsList.find(p => p._id === productId);
                            let formattedProductName = productName;
                            
                            if (productDetails) {
                                // Format: Company Name - Brand Name - Product Name
                                const companyName = productDetails.companyName || '';
                                const brandName = productDetails.brandName || (productDetails.brandId ? brandsList.find(b => b._id === productDetails.brandId)?.brandName : '') || '';
                                const prodName = productDetails.productName || productName;
                                
                                formattedProductName = [companyName, brandName, prodName]
                                    .filter(name => name && name.trim())
                                    .join(' - ');
                            }
                            
                            processedData.push({
                                productId,
                                productName: formattedProductName,
                                couponCode,
                                maxRedemptionCount,
                                maxCouponCount,
                                typeOfDiscount: typeOfDiscount.toLowerCase(),
                                discountValue,
                                pointsRequired
                            });
                        }
                    }
                });

                if (processedData.length === 0) {
                    toast.error('No valid data found in the Excel file');
                    return;
                }

                // Validate the data
                validateUploadedData(processedData);

            } catch (error) {
                console.error('Error processing Excel file:', error);
                toast.error('Error processing Excel file. Please check the file format.');
            }
        };

        reader.readAsArrayBuffer(file);
    };

    // Function to validate uploaded data
    const validateUploadedData = (data) => {
        let validRows = 0;
        let invalidRows = 0;

        // Get coupon code format from campaign
        const couponCodeFormat = campaignDetails?.campaign?.couponCodeFormat?.toLowerCase();

        const validatedData = data.map(row => {
            const errors = [];
            let isValid = true;

            // Validate coupon code format
            if (row.couponCode) {
                const codes = row.couponCode.split(',').map(code => code.trim());
                codes.forEach(code => {
                    if (!/^[A-Z0-9_-]+$/i.test(code)) {
                        errors.push(`Invalid format for code "${code}"`);
                        isValid = false;
                    }
                    if (code.length < 3 || code.length > 20) {
                        errors.push(`Code "${code}" must be between 3 and 20 characters`);
                        isValid = false;
                    }
                });
            }

            // Validate type of discount
            if (row.typeOfDiscount) {
                const validDiscountTypes = ['percentage', 'flat'];
                if (!validDiscountTypes.includes(row.typeOfDiscount)) {
                    errors.push(`Invalid discount type "${row.typeOfDiscount}". Must be one of: percentage or flat`);
                    isValid = false;
                }
            }

            // Validate discount value
            if (row.discountValue) {
                const discountNum = parseFloat(row.discountValue);
                if (isNaN(discountNum) || discountNum < 0) {
                    errors.push(`Invalid discount value "${row.discountValue}". Must be a positive number`);
                    isValid = false;
                }
                // Additional validation for percentage type
                if (row.typeOfDiscount && row.typeOfDiscount.toLowerCase() === 'percentage' && discountNum > 100) {
                    errors.push(`Percentage discount cannot be more than 100%`);
                    isValid = false;
                }
            }

            // Validate max redemption count - only for universal type
            if (couponCodeFormat === 'universal') {
                if (row.maxRedemptionCount !== undefined && row.maxRedemptionCount !== null && row.maxRedemptionCount !== '') {
                    const redemptionCount = parseInt(row.maxRedemptionCount);
                    if (isNaN(redemptionCount) || redemptionCount <= 0) {
                        errors.push(`Invalid max redemption count "${row.maxRedemptionCount}". Must be a positive integer for universal coupon type`);
                        isValid = false;
                    }
                }
            } else {
                // For unique type, allow 0 or positive values
                if (row.maxRedemptionCount !== undefined && row.maxRedemptionCount !== null && row.maxRedemptionCount !== '') {
                    const redemptionCount = parseInt(row.maxRedemptionCount);
                    if (isNaN(redemptionCount) || redemptionCount < 0) {
                        errors.push(`Invalid max redemption count "${row.maxRedemptionCount}". Must be a non-negative integer`);
                        isValid = false;
                    }
                }
            }

            // Validate max coupon count - only for unique type
            if (couponCodeFormat === 'unique') {
                if (row.maxCouponCount !== undefined && row.maxCouponCount !== null && row.maxCouponCount !== '') {
                    const couponCount = parseInt(row.maxCouponCount);
                    if (isNaN(couponCount) || couponCount <= 0) {
                        errors.push(`Invalid max coupon count "${row.maxCouponCount}". Must be a positive integer for unique coupon type`);
                        isValid = false;
                    }
                }
            } else {
                // For universal type, allow 0 or positive values
                if (row.maxCouponCount !== undefined && row.maxCouponCount !== null && row.maxCouponCount !== '') {
                    const couponCount = parseInt(row.maxCouponCount);
                    if (isNaN(couponCount) || couponCount < 0) {
                        errors.push(`Invalid max coupon count "${row.maxCouponCount}". Must be a non-negative integer`);
                        isValid = false;
                    }
                }
            }

            // Validate points required
            if (row.pointsRequired !== undefined && row.pointsRequired !== null && row.pointsRequired !== '') {
                const pointsReq = parseInt(row.pointsRequired);
                if (isNaN(pointsReq) || pointsReq < 0) {
                    errors.push(`Invalid points required "${row.pointsRequired}". Must be a non-negative integer`);
                    isValid = false;
                }
            }

            if (isValid) validRows++;
            else invalidRows++;

            return {
                ...row,
                isValid,
                validationErrors: errors
            };
        });

        setUploadedCouponData(validatedData);
        setValidationSummary({
            totalRows: data.length,
            validRows,
            invalidRows
        });

        toast.success(`Data loaded: ${data.length} total rows, ${validRows} valid, ${invalidRows} invalid`);
    };

    // Function to clear uploaded file and codes
    const clearUploadedCodes = () => {
        setUploadedFile(null);
        setCouponCodes([]);
        setUploadedCouponData([]);
        setValidationSummary({ totalRows: 0, validRows: 0, invalidRows: 0 });
        setValue('couponCode', '');
        // Reset file input
        const fileInput = document.getElementById('excel-upload');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Function to handle row editing
    const handleEditRow = (index) => {
        setEditingRowIndex(index);
        setEditingCouponCode(uploadedCouponData[index].couponCode);
        setEditingBrandName(uploadedCouponData[index].brandName || '');
        setEditingTypeOfDiscount(uploadedCouponData[index].typeOfDiscount);
        setEditingDiscountValue(uploadedCouponData[index].discountValue);
        setEditingMaxRedemptionCount(uploadedCouponData[index].maxRedemptionCount);
        setEditingMaxCouponCount(uploadedCouponData[index].maxCouponCount);
        setEditingPointsRequired(uploadedCouponData[index].pointsRequired);
    };

    // Function to save edited row
    const handleSaveEdit = (index) => {
        const updatedData = [...uploadedCouponData];
        updatedData[index] = {
            ...updatedData[index],
            couponCode: editingCouponCode,
            brandName: editingBrandName,
            typeOfDiscount: editingTypeOfDiscount,
            discountValue: editingDiscountValue,
            maxRedemptionCount: editingMaxRedemptionCount,
            maxCouponCount: editingMaxCouponCount,
            pointsRequired: editingPointsRequired
        };
        
        // Re-validate the updated data
        validateUploadedData(updatedData);
        setEditingRowIndex(null);
        setEditingCouponCode('');
        setEditingBrandName('');
        setEditingTypeOfDiscount('');
        setEditingDiscountValue('');
        setEditingMaxRedemptionCount('');
        setEditingMaxCouponCount('');
        setEditingPointsRequired('');
    };

    // Function to cancel editing
    const handleCancelEdit = () => {
        setEditingRowIndex(null);
        setEditingCouponCode('');
        setEditingBrandName('');
        setEditingTypeOfDiscount('');
        setEditingDiscountValue('');
        setEditingMaxRedemptionCount('');
        setEditingMaxCouponCount('');
        setEditingPointsRequired('');
    };

    // Function to delete row
    const handleDeleteRow = (index) => {
        const updatedData = uploadedCouponData.filter((_, i) => i !== index);
        if (updatedData.length === 0) {
            clearUploadedCodes();
        } else {
            validateUploadedData(updatedData);
        }
    };

    // Function to download sample Excel template
    const downloadTemplate = () => {
        const templateUrl = campaignDetails?.url;
        
        if (templateUrl) {
            // Create a temporary link element to download the file
            const link = document.createElement('a');
            link.href = templateUrl;
            link.download = 'coupon_codes_template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Template downloaded successfully!');
        } else {
            // Check if this is a brand promotion campaign
            const promotionType = campaignDetails?.campaign?.promotionType;
            const isBrandPromotion = promotionType === 'brand';
            
            if (isBrandPromotion) {
                // For brand promotion, use the selected brand
                const brandId = campaignDetails?.campaign?.brand;
                if (!brandId) {
                    toast.error('No brand found in the selected campaign');
                    return;
                }

                const brand = brandsList.find(b => b._id === brandId);
                if (!brand) {
                    toast.error('Brand details not found');
                    return;
                }

                // Create header row for brand promotion
                const templateData = [
                    ['BrandId', 'Brand Name', 'Coupon Code', 'Max Redemption Count', 'Max Coupon Count', 'Type of Discount(flat/percentage)', 'Discount Value', 'Points Required']
                ];

                // Add single row for the brand
                templateData.push([
                    brandId,
                    brand.brandName || 'Brand Name',
                    '', // Empty coupon code for admin to fill
                    '0', // Default max redemption count
                    '10', // Default max coupon count
                    'percentage', // Default type of discount
                    '10', // Default discount value
                    '1500' // Default points required
                ]);

                const ws = XLSX.utils.aoa_to_sheet(templateData);
                
                // Set column widths for better readability
                ws['!cols'] = [
                    { wch: 25 }, // BrandId
                    { wch: 50 }, // Brand Name
                    { wch: 30 }, // Coupon Code
                    { wch: 20 }, // Max Redemption Count
                    { wch: 18 }, // Max Coupon Count
                    { wch: 25 }, // Type of Discount(flat/percentage)
                    { wch: 15 }, // Discount Value
                    { wch: 15 }  // Points Required
                ];
                
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Coupon Codes");

                // Save the file
                XLSX.writeFile(wb, "brand_coupon_codes_template.xlsx");
                toast.success('Brand template downloaded successfully!');
            } else {
                // Create template with product information from campaign
                const productIds = campaignDetails?.campaign?.productServices || [];
                
                if (productIds.length === 0) {
                    toast.error('No products found in the selected campaign');
                    return;
                }

                // Create header row for product promotion
                const templateData = [
                    ['ProductId', 'Product Name', 'Coupon Code', 'Max Redemption Count', 'Max Coupon Count', 'Type of Discount(flat/percentage)', 'Discount Value', 'Points Required']
                ];

                // Add rows for each product with actual product details
                productIds.forEach(productId => {
                    const product = productsList.find(p => p._id === productId);
                    let productName = 'Product Name will be filled automatically';
                    
                    if (product) {
                        // Format: Company Name - Brand Name - Product Name
                        const companyName = product.companyName || '';
                        const brandName = product.brandName || (product.brandId ? brandsList.find(b => b._id === product.brandId)?.brandName : '') || '';
                        const prodName = product.productName || '';
                        
                        productName = [companyName, brandName, prodName]
                            .filter(name => name && name.trim())
                            .join(' - ');
                    }
                    
                    templateData.push([
                        productId,
                        productName,
                        '', // Empty coupon code for admin to fill
                        '0', // Default max redemption count
                        '10', // Default max coupon count
                        'percentage', // Default type of discount
                        '10', // Default discount value
                        '1500' // Default points required
                    ]);
                });

                const ws = XLSX.utils.aoa_to_sheet(templateData);
                
                // Set column widths for better readability
                ws['!cols'] = [
                    { wch: 25 }, // ProductId
                    { wch: 50 }, // Product Name
                    { wch: 30 }, // Coupon Code
                    { wch: 20 }, // Max Redemption Count
                    { wch: 18 }, // Max Coupon Count
                    { wch: 25 }, // Type of Discount(flat/percentage)
                    { wch: 15 }, // Discount Value
                    { wch: 15 }  // Points Required
                ];
                
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Coupon Codes");

                // Save the file
                XLSX.writeFile(wb, "coupon_codes_template.xlsx");
                toast.success('Template downloaded successfully!');
            }
        }
    };

    // Function to validate coupon codes
    const validateCouponCodes = (codes) => {
        const errors = [];
        const duplicates = [];
        const seen = new Set();

        codes.forEach((code, index) => {
            // Check for duplicates
            if (seen.has(code.toUpperCase())) {
                duplicates.push(code);
            } else {
                seen.add(code.toUpperCase());
            }

            // check if the typeOfDiscount is in the lowercase
            if(code.typeOfDiscount){
                code.typeOfDiscount = code.typeOfDiscount.toLowerCase();
            }

            // Check for valid format (alphanumeric and certain special characters)
            if (!/^[A-Z0-9_-]+$/i.test(code)) {
                errors.push(`Invalid format for code "${code}" at row ${index + 1}`);
            }

            // Check length (between 3 and 20 characters)
            if (code.length < 3 || code.length > 20) {
                errors.push(`Code "${code}" must be between 3 and 20 characters`);
            }
        });

        if (duplicates.length > 0) {
            errors.push(`Duplicate codes found: ${duplicates.join(', ')}`);
        }

        return { isValid: errors.length === 0, errors };
    };

    const onSubmit = (data) => {
        if (!selectedCampaignId) {
            toast.error('Please select a campaign first');
            return;
        }

        if (uploadedCouponData.length === 0) {
            toast.error('Please upload an Excel file with coupon codes');
            return;
        }

        if (validationSummary.invalidRows > 0) {
            toast.error('Please fix all validation errors before submitting');
            return;
        }

        // Process uploaded data to create individual coupon entries
        const processedCoupons = [];
        const isBrandPromotion = campaignDetails?.campaign?.promotionType === 'brand';
        
        uploadedCouponData.forEach(row => {
            const codes = row.couponCode.split(',').map(code => code.trim()).filter(code => code.length > 0);
            codes.forEach(code => {
                if (isBrandPromotion) {
                    processedCoupons.push({
                        brandId: row.brandId || null,
                        brandName: row.brandName,
                        couponCode: code,
                        typeOfDiscount: row.typeOfDiscount.toLowerCase(),
                        discountValue: row.discountValue,
                        maxRedemptionCount: row.maxRedemptionCount,
                        maxCouponCount: row.maxCouponCount,
                        pointsRequired: row.pointsRequired
                    });
                } else {
                    processedCoupons.push({
                        productId: row.productId || null,
                        brandId: row.brandId || null,
                        productName: row.productName,
                        couponCode: code,
                        typeOfDiscount: row.typeOfDiscount.toLowerCase(),
                        discountValue: row.discountValue,
                        maxRedemptionCount: row.maxRedemptionCount,
                        maxCouponCount: row.maxCouponCount,
                        pointsRequired: row.pointsRequired
                    });
                }
            });
        });

        const finalData = {
            // active: active,
            // couponCodeArray: processedCoupons.map(c => c.couponCode),
            // totalCodes: processedCoupons.length,
            // rawData: uploadedCouponData, // Store raw uploaded data
            campaignId: selectedCampaignId,
            couponArr: processedCoupons, // Store processed coupon data
            // Include campaign details from the selected campaign
            // productId: campaignDetails?.campaign?.productIdsArray?.[0] || null,
            // typeOfDiscount: campaignDetails?.campaign?.typeOfDiscount || 'percentage',
            // voucherType: campaignDetails?.campaign?.couponCodeFormat || 'universal',
            // discountValue: campaignDetails?.campaign?.discountValue || 0,
            // pointsToRedeem: campaignDetails?.campaign?.pointsToBeUsed || 0,
            // expiryDate: campaignDetails?.campaign?.endDate || null,
            // challengeId: campaignDetails?.campaign?.challengeId || null,
            // maxUseCount: campaignDetails?.campaign?.maxRedemptionCount || null,
        }

        console.log('Processed coupon data:', finalData);
        
        // if (couponId) {
        //     finalData.code = processedCoupons[0]?.couponCode || '';
        //     finalData.id = couponId;
        //     updateCoupon(finalData, {
        //         onSuccess: (res) => {
        //             if (res.error) {
        //                 console.log(res);
        //                 toast.error(res.error);
        //             } else {
        //                 toast.success('Coupon updated successfully!');
        //                 setTimeout(() => {
        //                     navigate('/coupons/list-coupon');
        //                 }, 2000);
        //             }
        //         },
        //         onError: (error) => {
        //             console.error('Error updating coupon:', error);
        //             toast.error('Failed to update coupon. Please try again.');
        //         }
        //     });
        // } else {
            createCoupon(finalData, {
                onSuccess: (res) => {
                    if (res.error) {
                        console.log(res);
                        toast.error(res.error);
                    } else {
                        toast.success('Coupons created successfully!');
                        setTimeout(() => {
                            navigate('/coupons/list-coupon');
                        }, 2000);
                    }
                },
                onError: (error) => {
                    console.error('Error creating coupons:', error);
                    toast.error('Failed to create coupons. Please try again.');
                }
            });
        // }
    };

    useEffect(() => {
        setTimeout(() => {
            if (couponData) {
                setActive(couponData.active);
                setValue("campaign", couponData.campaignId || '');
                setValue('couponCode', couponData.code);
                // Set the campaign ID to trigger loading campaign details
                if (couponData.campaignId) {
                    setSelectedCampaignId(couponData.campaignId);
                }
            }
        }, 200);
    }, [couponData]);

    if (isCouponDataLoading || !campaigns) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-black">Loading...</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full font-semibold text-base overflow-visible">
            <div className='flex items-center justify-end'>
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                    isCreateForm={true}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full font-semibold overflow-visible">
                <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Campaign Selection */}
                        <div className='flex flex-col gap-1 col-span-2'>
                            <label htmlFor="campaign" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                <span className="text-red text-xs inline">* </span>
                                Campaign
                            </label>
                            <NewDropdown
                                name={"campaign"}
                                dataList={campaigns}
                                value={watchedCampaign}
                                placeholder={'Select Campaign'}
                                onChange={(e) => handleCampaignChange(e.value)}
                            />
                            {errors.campaign && <p className="text-red text-base mt-1">{errors.campaign.message}</p>}
                        </div>

                        {/* Show message when no campaign is selected */}
                        {!selectedCampaignId && (
                            <div className="col-span-2 bg-green-50 border border-border rounded-md p-4">
                                <p className="text-secondary text-base font-medium">
                                    Please select a campaign to view its details and create coupons.
                                </p>
                            </div>
                        )}

                        {/* Campaign Details - Show only when campaign is selected */}
                        {selectedCampaignId && (
                            <>
                                {isCampaignDetailsLoading ? (
                                    <div className="col-span-2 flex items-center justify-center py-8">
                                        <div className="text-gray-600">Loading campaign details...</div>
                                    </div>
                                ) : campaignDetails ? (
                                    <>
                                        {/* Start Date */}
                                        <div className='flex flex-col gap-1'>
                                            <label className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                                Start Date
                                            </label>
                                            <div className="flex gap-2.5 items-center w-full bg-gray-100 rounded-md border border-solid border-navBtn text-gray-600">
                                                <input
                                                    type="text"
                                                    value={campaignDetails?.campaign?.startDate ? new Date(campaignDetails.campaign.startDate).toLocaleDateString() : 'Not specified'}
                                                    disabled
                                                    className="flex-1 shrink self-stretch my-auto basis-0 px-4 py-2.5 rounded-md text-base bg-gray-100 cursor-not-allowed"
                                                />
                                            </div>
                                        </div>

                                        {/* Valid Until */}
                                        <div className='flex flex-col gap-1'>
                                            <label className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                                Valid Until
                                            </label>
                                            <div className="flex gap-2.5 items-center w-full bg-gray-100 rounded-md border border-solid border-navBtn text-gray-600">
                                                <input
                                                    type="text"
                                                    value={campaignDetails?.campaign?.endDate ? new Date(campaignDetails.campaign.endDate).toLocaleDateString() : 'Not specified'}
                                                    disabled
                                                    className="flex-1 shrink self-stretch my-auto basis-0 px-4 py-2.5 rounded-md text-base bg-gray-100 cursor-not-allowed"
                                                />
                                            </div>
                                        </div>

                                        {/* Challenge Name */}
                                        <div className='flex flex-col gap-1'>
                                            <label className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                                Challenge Name
                                            </label>
                                            <div className="flex gap-2.5 items-center w-full bg-gray-100 rounded-md border border-solid border-navBtn text-gray-600">
                                                <input
                                                    type="text"
                                                    value={campaignDetails?.campaign?.challengeId?.name || 'Not specified'}
                                                    disabled
                                                    className="flex-1 shrink self-stretch my-auto basis-0 px-4 py-2.5 rounded-md text-base bg-gray-100 cursor-not-allowed"
                                                />
                                            </div>
                                        </div>

                                        {/* Coupon Code Format */}
                                        <div className='flex flex-col gap-1'>
                                            <label className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                                Coupon Code Format
                                            </label>
                                            <div className="flex gap-2.5 items-center w-full bg-gray-100 rounded-md border border-solid border-navBtn text-gray-600">
                                                <input
                                                    type="text"
                                                    value={campaignDetails?.campaign?.couponCodeFormat || 'Not specified'}
                                                    disabled
                                                    className="flex-1 shrink self-stretch my-auto basis-0 px-4 py-2.5 rounded-md text-base bg-gray-100 cursor-not-allowed"
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="col-span-2 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                        <p className="text-yellow-700 text-base font-medium">
                                            No campaign details found for the selected campaign.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Coupon Code - Only show when campaign is selected and details are loaded */}
                        {selectedCampaignId && !isCampaignDetailsLoading && (
                            <>
                                <div className="flex flex-col shrink-0 basis-0 col-span-2 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Coupon Code(s)
                                    </label>

                                    {/* Excel Upload Section */}
                                    <div className="mb-3">
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <label htmlFor="excel-upload" className="cursor-pointer bg-primary hover:bg-primaryHoverDark text-white px-3 py-2 rounded-md text-base font-bold transition-colors">
                                                Upload Excel File
                                            </label>
                                            <button
                                                type="button"
                                                onClick={downloadTemplate}
                                                className="bg-primary hover:bg-primaryHoverDark text-white px-3 py-2 rounded-md text-base font-bold transition-colors"
                                            >
                                                Download Template
                                            </button>
                                            <input
                                                id="excel-upload"
                                                type="file"
                                                accept=".xlsx,.xls"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                            />
                                            {uploadedFile && (
                                                <button
                                                    type="button"
                                                    onClick={clearUploadedCodes}
                                                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-base font-medium transition-colors"
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>

                                        {uploadedFile && (
                                            <div className="bg-green-50 border border-primary rounded-md p-3 shadow-sm">
                                                <div className="flex items-center gap-2 text-primary">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-base font-medium">
                                                        File uploaded: {uploadedFile.name}
                                                    </span>
                                                </div>
                                                <p className="text-base text-primary mt-1">
                                                    {validationSummary.totalRows} total rows, {validationSummary.validRows} valid rows, {validationSummary.invalidRows} invalid rows
                                                </p>
                                            </div>
                                        )}

                                        <p className="text-xs text-gray-500 mt-1">
                                            {campaignDetails?.campaign?.promotionType === 'brand' 
                                                ? 'Upload an Excel file with BrandId, Brand Name, Coupon Code, Max Redemption Count, Max Coupon Count, Type of Discount(flat/percentage), Discount Value, and Points Required. Click "Download Template" for the correct format.'
                                                : 'Upload an Excel file with ProductId, Product Name, Coupon Code, Max Redemption Count, Max Coupon Count, Type of Discount(flat/percentage), Discount Value, and Points Required. Click "Download Template" for the correct format.'
                                            }
                                        </p>

                                        {/* Validation Rules Info */}
                                        {campaignDetails?.campaign?.couponCodeFormat && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-2">
                                                <h4 className="text-blue-800 font-medium text-sm mb-1">
                                                    Validation Rules for {campaignDetails.campaign.couponCodeFormat} Coupon Type:
                                                </h4>
                                                <ul className="text-blue-700 text-xs list-disc list-inside space-y-1">
                                                    {campaignDetails.campaign.couponCodeFormat.toLowerCase() === 'unique' ? (
                                                        <>
                                                            <li>Max Coupon Count must be greater than 0</li>
                                                            <li>Max Redemption Count can be 0 or greater</li>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <li>Max Redemption Count must be greater than 0</li>
                                                            <li>Max Coupon Count can be 0 or greater</li>
                                                        </>
                                                    )}
                                                    <li>Points Required must be 0 or greater</li>
                                                    <li>Discount Value must be positive</li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    {/* Uploaded Data Table */}
                                    {uploadedCouponData.length > 0 && (
                                        <div className="mt-4">
                                            <div className="bg-white border border-secondary rounded-lg overflow-hidden">
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-secondary">
                                                        <thead className="bg-primary text-white font-extrabold">
                                                            <tr>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    {campaignDetails?.campaign?.promotionType === 'brand' ? 'Brand Name' : 'Product Name'}
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Coupon Code
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Max Redemption Count
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Max Coupon Count
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Type of Discount
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Discount Value
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Points Required
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Status
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                    Actions
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-secondary">
                                                            {uploadedCouponData.map((row, index) => (
                                                                <tr 
                                                                    key={index} 
                                                                    className={`${!row.isValid ? 'bg-redBg' : 'bg-primaryBg'}`}
                                                                >
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                                                                        {campaignDetails?.campaign?.promotionType === 'brand' ? row.brandName : row.productName}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                                                                        {editingRowIndex === index ? (
                                                                            <input
                                                                                type="text"
                                                                                value={editingCouponCode}
                                                                                onChange={(e) => setEditingCouponCode(e.target.value)}
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded-md text-base"
                                                                            />
                                                                        ) : (
                                                                            row.couponCode
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                                                                        {editingRowIndex === index ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editingMaxRedemptionCount}
                                                                                onChange={(e) => setEditingMaxRedemptionCount(e.target.value)}
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded-md text-base"
                                                                                min={campaignDetails?.campaign?.couponCodeFormat?.toLowerCase() === 'universal' ? "1" : "0"}
                                                                            />
                                                                        ) : (
                                                                            row.maxRedemptionCount
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                                                                        {editingRowIndex === index ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editingMaxCouponCount}
                                                                                onChange={(e) => setEditingMaxCouponCount(e.target.value)}
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded-md text-base"
                                                                                min={campaignDetails?.campaign?.couponCodeFormat?.toLowerCase() === 'unique' ? "1" : "0"}
                                                                            />
                                                                        ) : (
                                                                            row.maxCouponCount
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                                                                        {editingRowIndex === index ? (
                                                                            <select
                                                                                value={editingTypeOfDiscount}
                                                                                onChange={(e) => setEditingTypeOfDiscount(e.target.value)}
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded-md text-base"
                                                                            >
                                                                                <option value="percentage">percentage</option>
                                                                                <option value="flat">flat</option>
                                                                            </select>
                                                                        ) : (
                                                                            row.typeOfDiscount
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                                                                        {editingRowIndex === index ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editingDiscountValue}
                                                                                onChange={(e) => setEditingDiscountValue(e.target.value)}
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded-md text-base"
                                                                                min="0"
                                                                                step="0.01"
                                                                            />
                                                                        ) : (
                                                                            row.discountValue
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                                                                        {editingRowIndex === index ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editingPointsRequired}
                                                                                onChange={(e) => setEditingPointsRequired(e.target.value)}
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded-md text-base"
                                                                                min="0"
                                                                            />
                                                                        ) : (
                                                                            row.pointsRequired
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base">
                                                                        {row.isValid ? (
                                                                            <span className="inline-flex px-2 py-1 text-sm font-bold rounded-full bg-white shadow text-primary">
                                                                                Valid
                                                                            </span>
                                                                        ) : (
                                                                            <div>
                                                                                <span className="inline-flex px-2 py-1 text-sm font-bold rounded-full bg-white shadow text-red mb-1">
                                                                                    Invalid
                                                                                </span>
                                                                                <div className="text-xs text-red">
                                                                                    {row.validationErrors.join(', ')}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-base text-gray-500">
                                                                        {editingRowIndex === index ? (
                                                                            <div className="flex gap-2">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleSaveEdit(index)}
                                                                                    className="text-primary hover:text-primary text-xs"
                                                                                >
                                                                                    Save
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={handleCancelEdit}
                                                                                    className="text-gray-600 hover:text-gray-900 text-xs"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex gap-2">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleEditRow(index)}
                                                                                    className="text-blue-600 hover:text-chipBlue text-xs"
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleDeleteRow(index)}
                                                                                    className="text-red-600 hover:text-red text-xs"
                                                                                >
                                                                                    Delete
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {uploadedCouponData.length === 0 && (
                                        <div className="bg-yellow-50 border border-border rounded-md p-3 mt-2">
                                            <p className="text-secondary text-base">
                                                Please upload an Excel file with coupon codes to proceed.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                    </div>
                    {/* Form Actions */}
                    {selectedCampaignId && (
                        <div className="mt-10 flex gap-5 items-center justify-between">
                            <ActionButton
                                type="button"
                                variant="reset"
                                onClick={() => {
                                    reset();
                                    clearUploadedCodes();
                                    setSelectedCampaignId(null);
                                }}
                                text="Reset"
                                disabled={isSubmitting}
                            />
                            <ActionButton
                                type="submit"
                                variant="primary"
                                text={couponId ? 'Update Coupon' : 'Create Coupon'}
                                disabled={isCreating || isCampaignDetailsLoading || uploadedCouponData.length === 0 || validationSummary.invalidRows > 0}
                            />
                        </div>
                    )}
                </section>
            </form>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default CreateCoupons;